import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Switch,
  Upload,
  Typography,
  Divider,
} from "antd";
import { useFirebase, useFirestore } from "react-redux-firebase";
import { FIRESTORE_USERS_TABLE } from "../../constants/FirebaseConstants";
import styled from "styled-components";
import ImgCrop from "antd-img-crop";
import {
  STORAGE_AVATARS_LOCATION,
  STORAGE_PROFILE_IMAGES_LOCATION,
} from "../../constants/StorageConstants";
import { PROFESSIONS } from "../../constants/ProfessionConstants";
import { SKILLS } from "../../constants/SkillsConstants";
import { beforeImageUpload, dummyRequest } from "../../functions/Images";
import {
  DashboardFeedCard,
  DashboardSection,
  DashboardWrapper,
} from "../../styles/dashboardStyles";
import { colors } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import { PlusOutlined } from "@ant-design/icons";
import { ProFormTextArea } from "@ant-design/pro-components";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const Profile = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const firebase = useFirebase();
  const firestore = useFirestore();
  const storage = firebase.storage();
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const userProfile = useSelector((state) => state.firebaseReducer.profile);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [isPublic, setIsPublic] = useState(false);
  // State for handling profile images
  const [profileImages, setProfileImages] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!!userProfile) {
      let firstName;
      let lastName = "";
      if (!userProfile.firstName) {
        if (!!userProfile.fullName) {
          const splitFullName = userProfile.fullName.split(" ");
          firstName = splitFullName[0];
          splitFullName.forEach(
            (substring, index) =>
              index !== 0 &&
              (lastName =
                substring + (index !== splitFullName.length - 1 ? " " : "")),
          );
        }
      } else {
        firstName = userProfile.firstName;
        lastName = userProfile.lastName;
      }
      let profession;
      if (userProfile.profession === "MUSICIAN") {
        profession = "ARTIST";
      } else {
        profession = userProfile.profession;
      }

      const publicValue = !!userProfile.public;
      setIsPublic(publicValue);

      form.setFieldsValue({
        firstName: firstName,
        lastName: lastName,
        profession: profession,
        stageName: userProfile.stageName,
        email: userProfile.email,
        phone: userProfile.phone,
        skills: userProfile.skills,
        about: userProfile.about,
        externalLink: userProfile.externalLink,
        externalLinkText: userProfile.externalLinkText,
        linkedinLink: userProfile.linkedinLink,
        instagramLink: userProfile.instagramLink,
        tiktokLink: userProfile.tiktokLink,
        public: publicValue,
        publicName: userProfile.publicName,
        richBio: userProfile.richBio,
        showEmail: userProfile.showEmail || false,
        showPhone: userProfile.showPhone || false,
      });

      // Load profile images if they exist
      if (userProfile.profileImages && userProfile.profileImages.length > 0) {
        const loadedImageList = userProfile.profileImages.map((url, index) => ({
          uid: `-${index}`,
          name: `image-${index}.webp`,
          status: "done",
          url: url,
        }));
        setFileList(loadedImageList);
        setProfileImages(userProfile.profileImages);
      }

      syncImagesWithStorage();
    }
  }, [userProfile]);

  const handleSubmit = () => {
    setSaving(true);
    form
      .validateFields()
      .then((values) => {
        if (!values.skills) values.skills = [];
        if (!values.about) values.about = null;
        if (!values.externalLink) values.externalLink = null;
        if (!values.linkedinLink) values.linkedinLink = null;
        if (!values.instagramLink) values.instagramLink = null;
        if (!values.tiktokLink) values.tiktokLink = null;
        if (!values.externalLinkText) values.externalLinkText = null;
        if (!values.public) values.public = false;
        if (!values.stageName) values.stageName = null;
        if (!values.richBio) values.richBio = null;
        if (!values.showEmail) values.showEmail = false;
        if (!values.showPhone) values.showPhone = false;

        // Add profile images to the values
        values.profileImages = profileImages;

        // Validate public profile requirements
        if (values.public && !values.publicName) {
          message.error(t("public_name_required"));
          setSaving(false);
          return;
        }

        // Generate URL slug from publicName if it exists
        if (values.publicName) {
          // Create URL-friendly slug from public name
          const urlSlug = values.publicName
            .toLowerCase()
            .replace(/\s+/g, "-") // Replace spaces with hyphens
            .replace(/[^a-z0-9-]/g, "") // Remove special characters
            .replace(/-+/g, "-") // Replace multiple hyphens with a single one
            .replace(/^-|-$/g, ""); // Remove leading and trailing hyphens

          // Add the URL slug to the values
          values.urlSlug = urlSlug;
        }

        firestore
          .collection(FIRESTORE_USERS_TABLE)
          .doc(auth.uid)
          .update(values)
          .then(() => {
            message.success(t("profile_saved"));
            setSaving(false);
          })
          .catch((err) => {
            console.log(err);
            message.error(t("profile_save_error"));
            setSaving(false);
          });
      })
      .catch((err) => {
        console.log(err);
        setSaving(false);
      });
  };

  const saveAvatar = (file) => {
    const storageRef = storage.ref(STORAGE_AVATARS_LOCATION + "/" + auth.uid);
    storageRef.put(file.originFileObj).then((snapshot) => {
      storageRef.getDownloadURL().then((downloadUrl) => {
        firestore
          .collection(FIRESTORE_USERS_TABLE)
          .doc(auth.uid)
          .update({ avatar: downloadUrl })
          .then(() => message.success(t("notifications.avatar_saved")))
          .catch((err) => console.log(err));
      });
    });
  };

  const imageUploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    onChange(info) {
      if (info.file.status === "done") {
        saveAvatar(info.file);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };

  // Handle profile images upload
  const handleProfileImageChange = ({ fileList: newFileList }) => {
    // Check if the new file list exceeds the maximum count
    if (newFileList.length > 4) {
      message.error(t("max_images_error") || "Maximum 4 images allowed");
      return;
    }
    setFileList(newFileList);
  };

  const syncImagesWithStorage = async () => {
    try {
      // Get all images from storage
      const storageRef = storage.ref(
        `${STORAGE_PROFILE_IMAGES_LOCATION}/${auth.uid}`,
      );
      const result = await storageRef.listAll();

      // Get download URLs for all items
      const urlPromises = result.items.map((item) => item.getDownloadURL());
      const urls = await Promise.all(urlPromises);

      // If there's a mismatch between storage and profile
      if (
        userProfile.profileImages &&
        (urls.length !== userProfile.profileImages.length ||
          !urls.every((url) => userProfile.profileImages.includes(url)))
      ) {
        // Update Firestore with the correct images from storage
        await firestore
          .collection(FIRESTORE_USERS_TABLE)
          .doc(auth.uid)
          .update({ profileImages: urls });

        // Update local state
        setProfileImages(urls);

        // Update fileList for the Upload component
        const loadedImageList = urls.map((url, index) => ({
          uid: `-${index}`,
          name: `image-${index}.webp`,
          status: "done",
          url: url,
        }));
        setFileList(loadedImageList);
      }
    } catch (error) {
      console.error("Error syncing images with storage:", error);
    }
  };

  const uploadProfileImage = async (options) => {
    const { onSuccess, onError, file, onProgress } = options;

    try {
      // Create webp conversion function
      const convertToWebp = (file) => {
        return new Promise((resolve, reject) => {
          // Read the file
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;

            img.onload = () => {
              // Create canvas for webp conversion
              const canvas = document.createElement("canvas");
              canvas.width = img.width;
              canvas.height = img.height;

              // Draw image on canvas
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);

              // Convert to webp
              canvas.toBlob(
                (blob) => {
                  // Create a new file from the blob
                  const webpFile = new File(
                    [blob],
                    file.name.replace(/\.[^/.]+$/, ".webp"),
                    {
                      type: "image/webp",
                    },
                  );
                  resolve(webpFile);
                },
                "image/webp",
                0.8,
              ); // 0.8 quality
            };

            img.onerror = (error) => {
              reject(error);
            };
          };

          reader.onerror = (error) => {
            reject(error);
          };
        });
      };

      // Convert image to webp
      const webpFile = await convertToWebp(file);

      // Create a unique filename
      const fileName = `${auth.uid}_${Date.now()}.webp`;
      const storageRef = storage.ref(
        `${STORAGE_PROFILE_IMAGES_LOCATION}/${auth.uid}/${fileName}`,
      );

      // Upload file
      const uploadTask = storageRef.put(webpFile);

      // Monitor upload progress
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress({ percent: progress });
        },
        (error) => {
          console.error("Upload error:", error);
          onError(error);
        },
        () => {
          // Upload completed successfully, get download URL
          uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            // Update the profile images array
            const updatedImages = [...profileImages, downloadURL];
            setProfileImages(updatedImages);
            onSuccess(null, webpFile);
          });
        },
      );
    } catch (error) {
      console.error("Image conversion error:", error);
      onError(error);
    }
  };

  // Remove a profile image
  const handleRemoveImage = (file) => {
    // If the file has a URL, it's an already uploaded image
    if (file.url) {
      // First, create a reference to the file in Firebase Storage
      const fileRef = storage.refFromURL(file.url);

      // Delete the file from Firebase Storage
      fileRef
        .delete()
        .then(() => {
          // After successful deletion from Storage, update the profileImages array
          const updatedImages = profileImages.filter((url) => url !== file.url);
          setProfileImages(updatedImages);

          // Also update in Firestore
          firestore
            .collection(FIRESTORE_USERS_TABLE)
            .doc(auth.uid)
            .update({ profileImages: updatedImages })
            .then(() => message.success(t("image_removed")))
            .catch((err) => {
              console.error("Error removing image from Firestore:", err);
              message.error(t("error_removing_image"));
            });
        })
        .catch((error) => {
          console.error("Error removing image from Storage:", error);
          message.error(t("error_removing_image_storage"));
        });
    }
    return true; // Allow the removal from UI
  };

  // Handle public toggle change
  const handlePublicToggleChange = (checked) => {
    setIsPublic(checked);
  };

  return (
    <DashboardLayout>
      <StyledDashboardWrapper>
        <DashboardSection narrow>
          <StyledDashboardCard>
            <div className="card-header card-body p-4 w-100 border-0 d-flex">
              <div
                className={"pt-2 pb-2 d-flex align-items-center cursor-pointer"}
                onClick={() => navigate(-1)}
              >
                <i className="ti-arrow-left font-sm text-white"></i>
                <h4 className="font-xs text-white fw-600 ms-4 mb-0">
                  {t("go_back")}
                </h4>{" "}
              </div>
            </div>
            <div className="card-body p-lg-5 p-4 w-100 border-0 ">
              <div className="row justify-content-center">
                <div className="col-lg-4 text-center">
                  <ImgCrop
                    modalWidth={1000}
                    minZoom={0}
                    modalTitle={t("crop_image")}
                    modalOk={t("add")}
                    modalCancel={t("close")}
                    aspect={1}
                    cropperProps={{
                      restrictPosition: false,
                    }}
                  >
                    <Upload
                      {...imageUploadProps}
                      customRequest={dummyRequest}
                      beforeUpload={beforeImageUpload}
                      showUploadList={false}
                    >
                      <AvatarWrapper className="avatar ms-auto me-auto mb-0 mt-2 w100">
                        <img
                          src={
                            !!userProfile.avatar
                              ? userProfile.avatar
                              : "/assets/images/user/placeholder.png"
                          }
                          alt="avatar"
                          className="shadow-sm rounded-3 w-100"
                        />
                      </AvatarWrapper>
                    </Upload>
                  </ImgCrop>
                  <h2 className="fw-700 font-sm text-grey-900 mt-3">
                    {!userProfile.firstName
                      ? userProfile.fullName
                      : userProfile.firstName + " " + userProfile.lastName}
                  </h2>
                  {userProfile.stageName ? (
                    <h4
                      className="fw-500 mb-3 font-xsss mb-4"
                      style={{ color: colors.secondary }}
                    >
                      {userProfile.stageName}
                    </h4>
                  ) : (
                    <h4 className="text-grey-500 fw-500 mb-3 font-xsss mb-4">
                      {userProfile.email}
                    </h4>
                  )}
                </div>
              </div>
              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="firstName"
                      label={t("first_name")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_first_name"),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="lastName"
                      label={t("last_name")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_last_name"),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="profession"
                      label={t("profession")}
                      rules={[
                        {
                          required: true,
                          message: t("form.select_profession"),
                        },
                      ]}
                    >
                      <Select placeholder={t("profession_placeholder")}>
                        {PROFESSIONS.map((item, index) => {
                          return (
                            <Option key={index} value={item.id}>
                              {item[lang]}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="stageName" label={t("stage_name")}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label={t("email")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_email"),
                        },
                        {
                          type: "email",
                          message: t("form.enter_valid_email"),
                        },
                      ]}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label={t("phone_number")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_phone"),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item
                      name="skills"
                      label={t("skills")}
                      tooltip={t("skills_explanation")}
                    >
                      <Select mode="tags" placeholder={t("skills_placeholder")}>
                        {SKILLS.map((item, index) => {
                          return (
                            <Option key={index} value={item.id}>
                              {item[lang]}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name="externalLink"
                      label={t("external_link")}
                      tooltip={t("external_link_explanation")}
                      normalize={(value) => {
                        if (value && !value.startsWith("https://")) {
                          return `https://${value.replace(/^https?:\/\//, "")}`;
                        }
                        return value;
                      }}
                      rules={[
                        {
                          type: "url",
                          message: t("form.enter_valid_url"),
                        },
                      ]}
                    >
                      <Input placeholder={t("external_link_placeholder")} />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name="externalLinkText"
                      label={t("external_link_text")}
                      tooltip={t("external_link_text_explanation")}
                    >
                      <Input
                        placeholder={t("external_link_text_placeholder")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name="linkedinLink"
                      label={t("linkedin_link")}
                      normalize={(value) => {
                        if (value && !value.startsWith("https://")) {
                          return `https://${value.replace(/^https?:\/\//, "")}`;
                        }
                        return value;
                      }}
                      rules={[
                        {
                          type: "url",
                          message: t("form.enter_valid_url"),
                        },
                      ]}
                    >
                      <Input placeholder={t("linkedin_link_placeholder")} />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name="instagramLink"
                      label={t("instagram_link")}
                      normalize={(value) => {
                        if (value && !value.startsWith("https://")) {
                          return `https://${value.replace(/^https?:\/\//, "")}`;
                        }
                        return value;
                      }}
                      rules={[
                        {
                          type: "url",
                          message: t("form.enter_valid_url"),
                        },
                      ]}
                    >
                      <Input placeholder={t("instagram_link_placeholder")} />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name="tiktokLink"
                      label={t("tiktok_link")}
                      normalize={(value) => {
                        if (value && !value.startsWith("https://")) {
                          return `https://${value.replace(/^https?:\/\//, "")}`;
                        }
                        return value;
                      }}
                      rules={[
                        {
                          type: "url",
                          message: t("form.enter_valid_url"),
                        },
                      ]}
                    >
                      <Input placeholder={t("tiktok_link_placeholder")} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item name="about" label={t("user_description")}>
                      <TextArea
                        rows={4}
                        showCount
                        maxLength={500}
                        placeholder={t("about_user_placeholder")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Form.Item
                      name="public"
                      label={t("public_profile")}
                      tooltip={t("public_profile_tooltip")}
                      valuePropName="checked"
                    >
                      <Switch
                        checkedChildren={t("yes")}
                        unCheckedChildren={t("no")}
                        onChange={handlePublicToggleChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Public profile fields - only shown when public toggle is on */}
                {isPublic && (
                  <>
                    <Divider orientation="left">
                      <Title level={4}>{t("public_profile_settings")}</Title>
                    </Divider>
                    <Text
                      type="secondary"
                      style={{ marginBottom: "16px", display: "block" }}
                    >
                      {t("public_profile_description")}
                    </Text>

                    <Col xs={24} lg={16}>
                      <Form.Item
                        name="showEmail"
                        label={t("show_email_publicly")}
                        tooltip={t("show_email_publicly_tooltip")}
                        valuePropName="checked"
                      >
                        <Switch
                          checkedChildren={t("yes")}
                          unCheckedChildren={t("no")}
                          disabled={!isPublic}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={16}>
                      <Form.Item
                        name="showPhone"
                        label={t("show_phone_publicly")}
                        tooltip={t("show_phone_publicly_tooltip")}
                        valuePropName="checked"
                      >
                        <Switch
                          checkedChildren={t("yes")}
                          unCheckedChildren={t("no")}
                          disabled={!isPublic}
                        />
                      </Form.Item>
                    </Col>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="publicName"
                          label={t("public_name")}
                          tooltip={t("public_name_tooltip")}
                          rules={[
                            {
                              required: isPublic,
                              message: t("form.enter_public_name"),
                            },
                          ]}
                          extra={t("public_name_format_hint")}
                        >
                          <Input placeholder={t("public_name_placeholder")} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24}>
                        <Form.Item
                          name="richBio"
                          label={t("rich_bio")}
                          tooltip={t("rich_bio_tooltip")}
                        >
                          <ProFormTextArea
                            fieldProps={{
                              rows: 8,
                              placeholder: t("rich_bio_placeholder"),
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24}>
                        <Form.Item
                          label={t("profile_images")}
                          tooltip={t("profile_images_tooltip")}
                        >
                          <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onChange={handleProfileImageChange}
                            customRequest={uploadProfileImage}
                            onRemove={handleRemoveImage}
                            accept="image/*"
                            beforeUpload={beforeImageUpload}
                            maxCount={4}
                          >
                            {fileList.length >= 4 ? null : (
                              <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>
                                  {t("upload")}
                                </div>
                              </div>
                            )}
                          </Upload>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                <Button
                  type={"primary"}
                  htmlType={"submit"}
                  loading={saving}
                  block
                >
                  {t("save")}
                </Button>
              </Form>
            </div>
          </StyledDashboardCard>
        </DashboardSection>
      </StyledDashboardWrapper>
    </DashboardLayout>
  );
};

const StyledDashboardWrapper = styled(DashboardWrapper)`
  @media only screen and ${device.maxSm} {
    margin-top: 0;
    padding-inline: 0;
  }
`;

const StyledDashboardCard = styled(DashboardFeedCard)`
  padding: 0;
  margin: 0;

  .card-header {
    background-color: ${colors.primaryLight};
  }
`;

const AvatarWrapper = styled.figure`
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }
`;

export default Profile;
